/**
 * Structure annotation properties — registration and getters.
 *
 * A STRUCTURE is a polygon-based annotation representing a beam or column
 * inside a room. The base polygon is extruded vertically by `structure_height`.
 *
 * Properties registered here:
 * - structure_polygon (FLOAT_ARRAY, PROJECT_GLOBAL) — flat [x1,y1,z1, ...] base ring
 * - structure_height (FLOAT, PROJECT_GLOBAL) — extrusion height; 0 means flat
 * - structure_kind (SELECT, PROJECT_GLOBAL) — BEAM | COLUMN
 * - structure_room_id (STRING, PROJECT_GLOBAL) — containing ROOM annotation id (nullable)
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { TypedFloatArrayProperty } from '../../reactive/TypedFloatArrayProperty';
import { StructureKind } from 'shared-types';
import type { Coordinate } from './RoomGeometryPropertyGetters';

/**
 * Numeric ConvertType.DISTANCE mirrored from the client. Hardcoded as 1 here
 * because potree-shared can't import the client's enum without dragging in
 * React. Matches the pattern used in `PointCloudPropertyGetters.ts`.
 */
const CONVERT_TYPE_DISTANCE = 1;

// --- Encode/decode helpers ---

function encodePolygon(coords: Coordinate[]): number[] {
  const arr = new Array(coords.length * 3);
  for (let i = 0; i < coords.length; i++) {
    arr[i * 3] = coords[i].x;
    arr[i * 3 + 1] = coords[i].y;
    arr[i * 3 + 2] = coords[i].z;
  }
  return arr;
}

function decodePolygon(raw: unknown): Coordinate[] {
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'number') {
    const coords: Coordinate[] = [];
    for (let i = 0; i + 2 < raw.length; i += 3) {
      coords.push({ x: raw[i], y: raw[i + 1], z: raw[i + 2] });
    }
    return coords;
  }
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object') {
    return raw as Coordinate[];
  }
  if (typeof raw === 'string') {
    try {
      return decodePolygon(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  return [];
}

// --- Registration ---

export function registerStructureProperties() {
  if (!propertyRegistry.isRegistered('structure_polygon')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'structure_polygon',
      name: 'Polygon',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [],
      annotationTypes: [AnnotationType.STRUCTURE],
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('structure_height')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'structure_height',
      name: 'height',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.STRUCTURE],
      description: 'Vertical extrusion height of the structure (0 for flat custom shapes)',
      ui: { group: 'information', order: 2, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('structure_kind')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'structure_kind',
      name: 'type',
      valueType: PropertyValueType.SELECT,
      defaultValue: StructureKind.COLUMN,
      annotationTypes: [AnnotationType.STRUCTURE],
      description: 'Subtype of the structure: beam or column',
      ui: { group: 'information', groupOrder: 0, order: 1, hiddenInGroup: true },
      validation: {
        options: [
          { value: StructureKind.BEAM, label: 'beam' },
          { value: StructureKind.COLUMN, label: 'column' },
        ],
      },
    });
  }

  // `structure_room_id` links the structure to the containing ROOM annotation
  // (analogous to `opening_room_ids` but single-valued). Hidden from the panel
  // because it's resolved at creation time and rarely edited directly.
  if (!propertyRegistry.isRegistered('structure_room_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'structure_room_id',
      name: 'room',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.STRUCTURE],
      description: 'Identifier of the ROOM annotation that contains this structure (or empty)',
      ui: { hidden: true },
    });
  }
}

registerStructureProperties();

// --- Getters ---

const structurePolygonCache = new Map<string, TypedFloatArrayProperty<Coordinate[]>>();

export function getStructurePolygonProperty(annotationId: string): TypedFloatArrayProperty<Coordinate[]> {
  const cached = structurePolygonCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('structure_polygon', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedFloatArrayProperty<Coordinate[]>(raw, encodePolygon, decodePolygon);
  structurePolygonCache.set(annotationId, typed);
  return typed;
}

export function getStructureHeightProperty(annotationId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('structure_height', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

export function getStructureKindProperty(annotationId: string): ReactiveProperty<StructureKind> {
  const service = getGlobalPropertyService();
  return service.getProperty('structure_kind', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<StructureKind>;
}

export function getStructureRoomIdProperty(annotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('structure_room_id', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}
